import { legLabel } from '@/components/leg-search'
import { jerseyDataUri } from '../../../../../shared/pkg/prepacks/jerseySvg.js'
import { JERSEY_NUMBERS_RETIRED } from '../../../../../shared/pkg/prepacks/templateBuilder.js'
import { legBetLabel, previewLegTeam, typedPlayerNumber } from './prepackCardModel'

const KIT_BUTTON = 'px-2 py-1 text-[11px] font-medium disabled:opacity-50'
const kitButton = (on) => `${KIT_BUTTON} ${on ? 'bg-[var(--prepacks-accent)] text-[var(--wf-color-on-action)]' : 'text-[var(--prepacks-muted)] hover:text-[var(--wf-color-text)]'}`

/**
 * The jersey choices for a pack whose template shows jerseys (#572): a shirt
 * number for each player leg (no provider supplies one, so the operator types
 * it) and, for each leg with a team, the home or alternate kit (no provider
 * says which a team wears on the day). They are saved as the leg's
 * `player_number` and `kit_variant`, which the server applies to the leg's
 * team; the thumbnails and the card preview mirror that at once. `legs` carry
 * `player` and the team the check resolved (none: no thumbnail, no kit choice).
 * `numbers` is false for a template that draws jerseys without numbers (#602):
 * the box then says so, since the number fields are gone. Live templates let
 * player legs carry the operator's shirt number.
 * `onChange(outcomeId, key, value)`.
 */
export default function PrepackJerseyNumbers({ legs, numbers = true, jerseyNumbersNeedResave = false, disabled, onChange }) {
  return (
    <fieldset className="grid w-full min-w-0 gap-2.5 rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] p-3">
      <legend className="px-1 text-xs font-semibold text-[var(--prepacks-muted)]">Jerseys</legend>
      {legs.length === 0 ? (
        <p className="text-[11px] text-[var(--prepacks-muted)]">No jerseys in this pack</p>
      ) : (
        <ul className="grid min-w-0 gap-2">
          {legs.map((leg) => {
              // The leg as its card row reads it (#865): the participant, then the bet.
              const name = String(leg.participant || '').trim() || legLabel(leg)
              const detail = legBetLabel(leg)
              const team = previewLegTeam(leg)
              const jersey = team ? jerseyDataUri(team, leg.sport) : ''
              const alternate = leg.kit_variant === 'alternate'
              return (
                <li key={leg.outcome_id} className="flex min-w-0 items-center gap-2.5">
                  {jersey ? <img src={jersey} alt="" className="h-7 w-7 shrink-0" /> : <span aria-hidden="true" className="h-7 w-7 shrink-0" />}
                  <span className="min-w-0 flex-1 text-xs">
                    <span title={name} className="block truncate font-medium text-[var(--wf-color-text)]">{name}</span>
                    {detail ? <span className="block truncate text-[11px] text-[var(--prepacks-muted)]">{detail}</span> : null}
                  </span>
                  {team ? (
                    <span role="group" aria-label={`Kit for ${name}`} className="flex shrink-0 overflow-hidden rounded border border-[var(--prepacks-border)]">
                      <button type="button" aria-pressed={!alternate} disabled={disabled} onClick={() => onChange(leg.outcome_id, 'kit_variant', '')} className={kitButton(!alternate)}>Home</button>
                      <button type="button" aria-pressed={alternate} aria-label="Alternate kit" title="Alternate kit" disabled={disabled} onClick={() => onChange(leg.outcome_id, 'kit_variant', 'alternate')} className={kitButton(alternate)}>Alt</button>
                    </span>
                  ) : null}
                  {leg.player ? (
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="off"
                      placeholder="No."
                      aria-label={`Shirt number for ${name}`}
                      value={leg.player_number || ''}
                      disabled={disabled}
                      onChange={(event) => onChange(leg.outcome_id, 'player_number', typedPlayerNumber(event.target.value))}
                      className="w-12 shrink-0 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-2 py-1 text-center text-xs tabular-nums text-[var(--wf-color-text)] outline-none placeholder:text-[var(--prepacks-muted)] focus:border-[var(--prepacks-accent-text)] disabled:opacity-50"
                    />
                  ) : <span aria-hidden="true" className="w-12 shrink-0" />}
                </li>
            )
          })}
        </ul>
      )}
      {numbers || JERSEY_NUMBERS_RETIRED ? null : <p className="text-[11px] text-[var(--prepacks-muted)]">{jerseyNumbersNeedResave ? 'Re-save this template in the Template Builder to enable shirt numbers.' : 'This template shows jerseys without numbers.'}</p>}
    </fieldset>
  )
}
